import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FaBuilding,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaUserTie,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { AxiosGetMethod, AxiosPutMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { setActivePage } from "../store/activePage";
import CitySearchSelect from "../components/city-search-select";

const getPasswordStrength = (password) => {
  const value = String(password || "");

  if (!value) {
    return {
      score: 0,
      label: "Add a new password",
      color: "bg-slate-200",
      textColor: "text-slate-400",
    };
  }

  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;

  if (score <= 1) {
    return {
      score,
      label: "Weak password",
      color: "bg-red-400",
      textColor: "text-red-500",
    };
  }

  if (score === 2 || score === 3) {
    return {
      score,
      label: "Medium password",
      color: "bg-amber-400",
      textColor: "text-amber-600",
    };
  }

  return {
    score,
    label: "Strong password",
    color: "bg-emerald-500",
    textColor: "text-emerald-600",
  };
};

const personalSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  occupation: Yup.string().required("Occupation is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
});

const organisationSchema = Yup.object({
  organisation_name: Yup.string().required("Organisation name is required"),
  address: Yup.string().required("Organisation address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  pincode: Yup.string()
    .matches(/^\d{6}$/, "Pincode must be 6 digits")
    .required("Pincode is required"),
});

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(6, "New password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

const inputClass =
  "w-full rounded-lg border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:ring-2";

const readOnlyClass =
  "no-translate w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500 outline-none";

const initialProfile = {
  personal_info: {
    first_name: "",
    last_name: "",
    mobile: "",
    email: "",
    occupation: "",
  },
  organisation_info: {
    organisation_name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  },
};

const OperatorSettings = () => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(initialProfile);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const orgName = localStorage.getItem("org-name") || "Organisation";

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Settings",
        activeMenu: "settingsPage",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      const res = await AxiosGetMethod("org/profile");
      if (res?.status === 200 && res?.data?.data) {
        setProfile({
          personal_info: {
            ...initialProfile.personal_info,
            ...(res.data.data.personal_info || {}),
          },
          organisation_info: {
            ...initialProfile.organisation_info,
            ...(res.data.data.organisation_info || {}),
          },
        });
      } else {
        setProfile((prev) => ({
          ...prev,
          personal_info: {
            ...prev.personal_info,
            first_name: localStorage.getItem("org-first-name") || prev.personal_info.first_name,
            last_name: localStorage.getItem("org-last-name") || prev.personal_info.last_name,
            email: localStorage.getItem("org-email") || prev.personal_info.email,
          },
          organisation_info: {
            ...prev.organisation_info,
            organisation_name: localStorage.getItem("org-name") || prev.organisation_info.organisation_name,
          },
        }));
      }
      setProfileLoading(false);
    };

    loadProfile();
  }, []);

  const personalFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: profile.pending_request?.requested_changes?.first_name || profile.personal_info.first_name || "",
      last_name: profile.pending_request?.requested_changes?.last_name || profile.personal_info.last_name || "",
      occupation: profile.pending_request?.requested_changes?.occupation || profile.personal_info.occupation || "",
      email: profile.pending_request?.requested_changes?.email || profile.personal_info.email || "",
      mobile: profile.personal_info.mobile || localStorage.getItem("org-phone") || "",
    },
    validationSchema: personalSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      const { mobile, ...personalValues } = values;
      const organisationValues = {
        organisation_name: profile.pending_request?.requested_changes?.organisation_name || profile.organisation_info.organisation_name || "",
        address: profile.pending_request?.requested_changes?.address || profile.organisation_info.address || "",
        city: profile.pending_request?.requested_changes?.city || profile.organisation_info.city || "",
        state: profile.pending_request?.requested_changes?.state || profile.organisation_info.state || "",
        pincode: profile.pending_request?.requested_changes?.pincode || profile.organisation_info.pincode || "",
      };
      const payload = {
        ...personalValues,
        ...organisationValues,
      };
      const res = await AxiosPutMethod("org/profile", payload);
      setSubmitting(false);

      if (res?.status === 200 && res?.data?.data) {
        const nextProfile = res.data.data;
        setProfile(nextProfile);
        SuccessToast("Personal profile changes submitted for admin approval");
        return;
      }

      if (res?.status === 409) {
        setFieldError("email", "This email is already registered.");
        ErrorToast(res?.data?.message || "This email is already registered.");
        return;
      }

      ErrorToast(res?.data?.message || "Unable to update personal information");
    },
  });

  const organisationFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      organisation_name: profile.pending_request?.requested_changes?.organisation_name || profile.organisation_info.organisation_name || "",
      address: profile.pending_request?.requested_changes?.address || profile.organisation_info.address || "",
      city: profile.pending_request?.requested_changes?.city || profile.organisation_info.city || "",
      state: profile.pending_request?.requested_changes?.state || profile.organisation_info.state || "",
      pincode: profile.pending_request?.requested_changes?.pincode || profile.organisation_info.pincode || "",
    },
    validationSchema: organisationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const personalValues = {
        first_name: profile.pending_request?.requested_changes?.first_name || profile.personal_info.first_name || "",
        last_name: profile.pending_request?.requested_changes?.last_name || profile.personal_info.last_name || "",
        occupation: profile.pending_request?.requested_changes?.occupation || profile.personal_info.occupation || "",
        email: profile.pending_request?.requested_changes?.email || profile.personal_info.email || "",
      };
      const payload = {
        ...personalValues,
        ...values,
      };
      const res = await AxiosPutMethod("org/profile", payload);
      setSubmitting(false);

      if (res?.status === 200 && res?.data?.data) {
        const nextProfile = res.data.data;
        setProfile(nextProfile);
        SuccessToast("Organisation profile changes submitted for admin approval");
        return;
      }

      ErrorToast(res?.data?.message || "Unable to update organisation information");
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm, setSubmitting, setFieldError }) => {
      const res = await AxiosPutMethod("org/change-password", {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });

      setSubmitting(false);

      if (res?.status === 200) {
        SuccessToast("Password updated successfully");
        resetForm();
        return;
      }

      if (res?.status === 400) {
        setFieldError("currentPassword", "Current password is incorrect");
        ErrorToast(res?.data?.message || "Unable to update password");
        return;
      }

      ErrorToast("Unable to update password right now");
    },
  });

  const passwordStrength = getPasswordStrength(passwordFormik.values.newPassword);

  return (
    <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-5">
        {profile.pending_request && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm animate-[fadeIn_0.25s_ease] flex items-start gap-4">
            <span className="text-xl shrink-0 mt-0.5">⏳</span>
            <div>
              <h4 className="text-sm font-bold text-amber-900">Changes Pending Approval</h4>
              <p className="mt-1 text-xs text-amber-800 leading-relaxed">
                You have submitted profile updates that are waiting for admin approval. 
                Below are the requested pending changes. You can modify and re-submit if needed.
              </p>
            </div>
          </div>
        )}

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
              <FaLock className="text-base" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-teal-600">
                Security and Profile
              </p>
              <h2 className="mt-1 text-2xl font-black text-slate-900">
                {orgName} Settings
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Update organisation details, personal information, and password
                from one place. Mobile number stays read-only.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <FaBuilding />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Organisation Information
              </h3>
              <p className="text-sm text-slate-500">
                Edit your organisation name and address details.
              </p>
            </div>
          </div>

          <form onSubmit={organisationFormik.handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                Organisation Name
              </label>
              <input
                name="organisation_name"
                value={organisationFormik.values.organisation_name}
                onChange={organisationFormik.handleChange}
                onBlur={organisationFormik.handleBlur}
                className={`force-tamil ${inputClass} ${
                  organisationFormik.touched.organisation_name &&
                  organisationFormik.errors.organisation_name
                    ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                    : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-100"
                }`}
                placeholder="Organisation name"
              />
              {organisationFormik.touched.organisation_name &&
                organisationFormik.errors.organisation_name && (
                  <p className="mt-2 text-xs font-semibold text-red-500">
                    {organisationFormik.errors.organisation_name}
                  </p>
                )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                Organisation Address
              </label>
              <textarea
                name="address"
                rows={3}
                value={organisationFormik.values.address}
                onChange={organisationFormik.handleChange}
                onBlur={organisationFormik.handleBlur}
                className={`force-tamil ${inputClass} ${
                  organisationFormik.touched.address &&
                  organisationFormik.errors.address
                    ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                    : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-100"
                }`}
                placeholder="Organisation address"
              />
              {organisationFormik.touched.address &&
                organisationFormik.errors.address && (
                  <p className="mt-2 text-xs font-semibold text-red-500">
                    {organisationFormik.errors.address}
                  </p>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  City
                </label>
                <CitySearchSelect
                  value={organisationFormik.values.city}
                  onChange={(city) => organisationFormik.setFieldValue("city", city)}
                  onBlur={() => organisationFormik.setFieldTouched("city", true)}
                  noAutoTranslate
                  placeholder="Search city"
                  className={`${inputClass} ${
                    organisationFormik.touched.city && organisationFormik.errors.city
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-100"
                  }`}
                />
                {organisationFormik.touched.city && organisationFormik.errors.city && (
                  <p className="mt-2 text-xs font-semibold text-red-500">
                    {organisationFormik.errors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  State
                </label>
                <input
                  name="state"
                  value={organisationFormik.values.state}
                  onChange={organisationFormik.handleChange}
                  onBlur={organisationFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    organisationFormik.touched.state &&
                    organisationFormik.errors.state
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-100"
                  }`}
                  placeholder="State"
                />
                {organisationFormik.touched.state && organisationFormik.errors.state && (
                  <p className="mt-2 text-xs font-semibold text-red-500">
                    {organisationFormik.errors.state}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Pincode
                </label>
                <input
                  name="pincode"
                  value={organisationFormik.values.pincode}
                  onChange={organisationFormik.handleChange}
                  onBlur={organisationFormik.handleBlur}
                  className={`no-translate ${inputClass} ${
                    organisationFormik.touched.pincode &&
                    organisationFormik.errors.pincode
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-100"
                  }`}
                  placeholder="6 digit pincode"
                />
                {organisationFormik.touched.pincode &&
                  organisationFormik.errors.pincode && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      {organisationFormik.errors.pincode}
                    </p>
                  )}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={organisationFormik.isSubmitting || profileLoading}
                className="rounded-lg bg-emerald-600 px-6 py-3 text-sm font-black text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {organisationFormik.isSubmitting ? "Updating..." : "Update Organisation"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
              <FaUserTie />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Personal Information
              </h3>
              <p className="text-sm text-slate-500">
                Update the owner details linked to this organisation account.
              </p>
            </div>
          </div>

          <form onSubmit={personalFormik.handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  First Name
                </label>
                <input
                  name="first_name"
                  value={personalFormik.values.first_name}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.first_name &&
                    personalFormik.errors.first_name
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-amber-500 focus:ring-amber-100"
                  }`}
                  placeholder="First name"
                />
                {personalFormik.touched.first_name &&
                  personalFormik.errors.first_name && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      {personalFormik.errors.first_name}
                    </p>
                  )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Last Name
                </label>
                <input
                  name="last_name"
                  value={personalFormik.values.last_name}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.last_name &&
                    personalFormik.errors.last_name
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-amber-500 focus:ring-amber-100"
                  }`}
                  placeholder="Last name"
                />
                {personalFormik.touched.last_name &&
                  personalFormik.errors.last_name && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      {personalFormik.errors.last_name}
                    </p>
                  )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Occupation
                </label>
                <input
                  name="occupation"
                  value={personalFormik.values.occupation}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.occupation &&
                    personalFormik.errors.occupation
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-amber-500 focus:ring-amber-100"
                  }`}
                  placeholder="Occupation"
                />
                {personalFormik.touched.occupation &&
                  personalFormik.errors.occupation && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      {personalFormik.errors.occupation}
                    </p>
                  )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Email
                </label>
                <input
                  name="email"
                  value={personalFormik.values.email}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`no-translate ${inputClass} ${
                    personalFormik.touched.email && personalFormik.errors.email
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : "border-slate-200 focus:border-amber-500 focus:ring-amber-100"
                  }`}
                  placeholder="Email address"
                />
                {personalFormik.touched.email && personalFormik.errors.email && (
                  <p className="mt-2 text-xs font-semibold text-red-500">
                    {personalFormik.errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Mobile Number
                </label>
                <input
                  value={personalFormik.values.mobile}
                  readOnly
                  className={readOnlyClass}
                />
                <p className="mt-2 text-xs font-semibold text-slate-500">
                  Mobile number cannot be edited from settings.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={personalFormik.isSubmitting || profileLoading}
                className="rounded-lg bg-amber-600 px-6 py-3 text-sm font-black text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {personalFormik.isSubmitting ? "Updating..." : "Update Personal Info"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
              <FaCheckCircle />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Reset Password
              </h3>
              <p className="text-sm text-slate-500">
                Choose a strong password with at least 6 characters.
              </p>
            </div>
          </div>

          <form onSubmit={passwordFormik.handleSubmit} className="space-y-5">
            {[
              {
                id: "currentPassword",
                label: "Current Password",
                value: passwordFormik.values.currentPassword,
                visible: showCurrentPassword,
                setVisible: setShowCurrentPassword,
                autoComplete: "current-password",
              },
              {
                id: "newPassword",
                label: "New Password",
                value: passwordFormik.values.newPassword,
                visible: showNewPassword,
                setVisible: setShowNewPassword,
                autoComplete: "new-password",
              },
              {
                id: "confirmPassword",
                label: "Confirm New Password",
                value: passwordFormik.values.confirmPassword,
                visible: showConfirmPassword,
                setVisible: setShowConfirmPassword,
                autoComplete: "new-password",
              },
            ].map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={field.id}
                  className="mb-2 block text-sm font-bold text-slate-700"
                >
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    id={field.id}
                    name={field.id}
                    type={field.visible ? "text" : "password"}
                    value={field.value}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    className={`no-translate ${inputClass} pr-12 ${
                      passwordFormik.touched[field.id] &&
                      passwordFormik.errors[field.id]
                        ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                        : "border-slate-200 focus:border-teal-500 focus:ring-teal-100"
                    }`}
                    autoComplete={field.autoComplete}
                  />
                  <button
                    type="button"
                    onClick={() => field.setVisible((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                    aria-label={field.visible ? "Hide password" : "Show password"}
                  >
                    {field.visible ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {passwordFormik.touched[field.id] &&
                  passwordFormik.errors[field.id] && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      {passwordFormik.errors[field.id]}
                    </p>
                  )}
                {field.id === "newPassword" && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-2 flex-1 rounded-full ${
                            passwordStrength.score >= level
                              ? passwordStrength.color
                              : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs font-semibold ${passwordStrength.textColor}`}>
                      {passwordStrength.label}
                    </p>
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={passwordFormik.isSubmitting}
                className="rounded-lg bg-slate-900 px-6 py-3 text-sm font-black text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {passwordFormik.isSubmitting ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default OperatorSettings;
